/**
 * WebAuthn wire-format helpers for the MFA proxy flow (#597 Phase 3).
 *
 * Cognito's SDK shape for ``StartWebAuthnRegistration`` /
 * ``CompleteWebAuthnRegistration`` encodes binary fields as base64url
 * strings, but the browser's WebAuthn API (``navigator.credentials.create``)
 * takes and returns ``ArrayBuffer``. This module owns the two-way
 * conversion + the shape of the intermediate JSON we pass over the wire.
 *
 * Base64url encoding is base64 with URL-safe substitutions (``+`` → ``-``,
 * ``/`` → ``_``) and no ``=`` padding — the same scheme JWT + WebAuthn
 * itself use. atob/btoa work on standard base64, so we do the
 * character substitution + padding ourselves.
 */

// --- Type shapes --------------------------------------------------------
//
// Kept as loose types because Cognito occasionally adds fields to the
// WebAuthn Level 3 spec (large-blob, minPinLength, hints). Narrowing
// here would break the SPA on any Cognito-side addition; we relay
// unknown fields untouched (spread the source object).

export interface CognitoWebAuthnOptions {
  challenge: string; // base64url
  rp: PublicKeyCredentialRpEntity;
  user: {
    id: string; // base64url
    name: string;
    displayName: string;
  };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  excludeCredentials?: Array<{
    id: string; // base64url
    type: PublicKeyCredentialType;
    transports?: AuthenticatorTransport[];
  }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
  extensions?: Record<string, unknown>;
}

/** JSON-safe shape POSTed to ``/api/auth/mfa/passkey/complete``. */
export interface CognitoWebAuthnAttestation {
  id: string;
  rawId: string; // base64url
  type: string;
  authenticatorAttachment: string | null;
  response: {
    clientDataJSON: string; // base64url
    attestationObject: string; // base64url
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

// --- Encoders / decoders -----------------------------------------------

/**
 * Decode a base64url string into an ``ArrayBuffer`` — the shape
 * ``navigator.credentials.create()`` expects for ``challenge``,
 * ``user.id``, and ``excludeCredentials[i].id``.
 *
 * base64url differs from base64 in three ways: ``-`` for ``+``, ``_``
 * for ``/``, and no ``=`` padding. We reverse those before handing to
 * ``atob``, which requires standard base64 with padding.
 *
 * Rupert PR-608 r0 ADVISORY #5: we validate the alphabet BEFORE
 * calling ``atob``. Standard base64's ``+`` / ``/`` chars, ``=``
 * padding, and (in some engines) ASCII whitespace all pass ``atob``
 * silently despite this function's ``base64url`` name. If Cognito
 * (or any future producer) ever ships a mis-formatted value, we want
 * a loud rejection here — not a silent decode into the wrong bytes.
 */
export function base64UrlToArrayBuffer(b64url: string): ArrayBuffer {
  // The base64url alphabet is A–Z, a–z, 0–9, ``-`` and ``_`` — no
  // ``=`` padding, no ``+``/``/``, no whitespace. The empty string
  // is a legitimate zero-byte input.
  if (!/^[A-Za-z0-9_-]*$/.test(b64url)) {
    throw new Error("base64UrlToArrayBuffer: input is not valid base64url");
  }
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "===".slice((b64.length + 3) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Encode an ``ArrayBuffer`` (from a WebAuthn ``AuthenticatorResponse``
 * binary field) into base64url for the JSON wire format.
 *
 * Long buffers are chunked before ``String.fromCharCode(...bytes)`` to
 * avoid ``RangeError: Maximum call stack size exceeded`` on large
 * attestation objects — the ceiling is ~65k bytes for most engines
 * and an attestation can legitimately push that.
 */
export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  const b64 = btoa(binary);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
